import {
  BodyExtraSmall,
  BodyLarge,
  BodyLargeBold,
  BodyRegularSemiBold,
  BodySmall,
  Heading6,
  BodySmallMedium,
  BodyRegularMedium,
} from '@src/fonts/typography/Typography';
import { LayoutColors } from '@src/core/theme';
import styled from 'styled-components';
import { device } from '@src/common/breakpoints';

interface PurchaseResumeContentProps {
  isCollapsed: boolean;
  isResponsive: boolean;
}

interface HeaderProps {
  isResponsive?: boolean;
}

export const Container = styled.article`
  width: 100%;
  max-height: 534px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${LayoutColors.gray};
  border-radius: 4px;

  ${device.mediumTablet} {
    max-height: 736px;
  }
`;

export const PackageProductWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > *:not(:last-child) {
    border-bottom: 1px solid ${LayoutColors.grayMain};
    padding-bottom: 16px;
  }
`;

export const Header = styled.div<HeaderProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 16px 16px 0;

  ${device.mediumTablet} {
    cursor: default;
  }
`;

export const ProductsResume = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;

  overflow: auto;
  padding: 0px 8px 0px 16px;
  margin-right: 16px;

  ${device.mediumTablet} {
    max-height: 590px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${LayoutColors.blackLight};
    border-radius: 10px;
  }
  &::-webkit-scrollbar {
    width: 10px;
  }
`;

export const ResumeTitle = styled.h2`
  ${BodyLarge}

  ${device.mediumTablet} {
    ${Heading6};
  }
`;

export const PurchaseResumeContent = styled.div<PurchaseResumeContentProps>`
  width: 100%;
  display: ${({ isCollapsed, isResponsive }) => (isResponsive && isCollapsed ? 'none' : 'block')};
`;

export const Product = styled.div`
  width: 100%;
  border-radius: 4px;
  border: 1px solid #cdcdcd;
  padding: 16px;

  &:last-of-type {
    margin-bottom: 16px;
  }
`;

export const ProductContent = styled.div`
  ${Product}

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const ProductHourAndDate = styled.h4`
  ${BodyRegularMedium};

  ${device.mediumTablet} {
    ${BodyRegularSemiBold};
  }
`;

export const ProductName = styled.p`
  margin-top: 16px;
  ${BodySmallMedium}
`;

export const Price = styled.p`
  margin-top: 4px;
  ${BodyExtraSmall}
`;

export const PurchaseResumeTotal = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  padding-right: 24px;

  width: 100%;
  background-color: ${LayoutColors.gray};
`;

export const TotalText = styled.p`
  ${BodyRegularMedium}
`;

export const TotalPrice = styled.p`
  ${BodyLargeBold}
`;

export const AdditionalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
`;

export const Coupon = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;

  ${BodySmall}
`;

export const CouponText = styled.p`
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
`;

export const Total = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

export const CouponValue = styled.p`
  ${BodySmall}
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

export const CouponContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CouponCode = styled.strong`
  ${BodySmall}
  font-weight: 700 !important;
`;

export const TotalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding-top: 8px;
  margin-top: 8px;
  border-top: 1px solid ${LayoutColors.grayMain};
`;

export const TotalWithoutDiscountContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const TotalWithoutDiscountHeader = styled.p`
  ${BodySmall}
`;

export const TotalWithoutDiscountValue = styled.p`
  ${BodySmallMedium}
  font-size: 14px;
  border-radius: 4px;
  padding: 2px 8px;
  border: 1px solid ${LayoutColors.grayMain};
`;

export const PackageDates = styled.p`
  ${BodySmallMedium}
  margin-top: 8px;
  color: ${LayoutColors.blackMain};
`;

export const PackageTariffInfo = styled.p`
  ${BodySmallMedium}
  margin-top: 8px;
`;

export const PackagePriceInfo = styled('div')`
  display: flex;
  justify-content: space-between;
  ${BodySmall}
`;

export const PackagePrices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
`;

export const PackageOriginalPrice = styled.span`
  color: ${LayoutColors.blackMain};
  ${BodySmallMedium}
`;

export const PackageDiscountValue = styled.span`
  color: ${LayoutColors.greenDark};
  ${BodySmallMedium}
`;

export const PackageTotalPrice = styled.span`
  ${BodyRegularMedium}
`;

export const PackageProductDetails = styled.div`
  ${BodySmallMedium}
`;

export const PackageProductDetailsContent = styled.div`
  padding: 8px;
  background-color: ${LayoutColors.gray};
`;

export const ProductProvider = styled.p`
  ${BodyExtraSmall};
  color: ${LayoutColors.blackMain};

  margin: 4px 0px;
`;
